import React, { useEffect, useRef } from "react";
import { Typography, Grid } from "@material-ui/core";
import Button from "../../../common/button/button";
import useTranslatedValue from "../../../../hooks/useTranslatedValue";
import useQuestionStyles from "./questionStyles";
import useFileQuestionManager from "../../hooks/useFileQuestionManager";
import QuestionAttachmentList from "../attachments/QuestionAttachmentList";
import { launchSketchTool } from "../../../admin/attachments/launchSketchTool";
import { useDispatch } from "react-redux";
import { ALERT_TYPES, showAlert } from "../../../../redux/alert/alertActions";
import { fileMessages } from "../../../admin/attachments/messages";

export const SketchQuestion = ({ question, isDisabled, followUpUUID }) => {
    const classes = useQuestionStyles();
    const getTranslatedValue = useTranslatedValue();
    const fileInputRef = useRef();
    const dispatch = useDispatch();

    const maxNumberOfFiles = question.props?.fileLimit ?? 10;
    const fileSizeLimit = question.props?.fileSize?.max;
    const { handleFileChange, handleFileUpdate, handleRemoveFile } = useFileQuestionManager(question, followUpUUID, maxNumberOfFiles, fileSizeLimit);

    const handleSketchClick = () => launchSketchTool(followUpUUID);

    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
            fileInputRef.current.click();
        }
    };

    const files = Array.isArray(question.value) ? question.value : [];
    const canAddMore = files.length < maxNumberOfFiles;

    useEffect(() => {
        if (canAddMore === false) {
            dispatch(showAlert(Date.now(), fileMessages.fileSizeLimit, ALERT_TYPES.error, { size: maxNumberOfFiles }));
        }
    }, [canAddMore]);

    return (
        <>
            <Typography variant="body1" className={classes.questionText}>
                {question.mandatory && <span className={classes.mandatory}>*</span>}
                {getTranslatedValue(question.questionText)}
            </Typography>
            {getTranslatedValue(question.tooltip) && (
                <Typography variant="caption" className={classes.tooltip}>
                    {getTranslatedValue(question.tooltip)}
                </Typography>
            )}
            <Grid container alignItems="center" style={{ marginTop: 8 }}>
                <Grid item className={classes.spaceBetweenButtons}>
                    <Button secondary onClick={handleSketchClick} disabled={isDisabled || question.isDisabled}>
                        {getTranslatedValue(question.props?.sketchButtonText) ||
                            getTranslatedValue({ en: "Launch Sketch Tool", fr: "Ouvrir l'outil de croquis" })}
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        style={{ opacity: isDisabled || question.isDisabled || !canAddMore ? 0.5 : 1 }}
                        secondary
                        onClick={handleUploadClick}
                        disabled={isDisabled || question.isDisabled || !canAddMore}>
                        {getTranslatedValue(question.props?.uploadButtonText, "Upload Sketch")}
                    </Button>
                </Grid>
                <input
                    ref={fileInputRef}
                    accept={question.props.fileFormats ?? "image/png"}
                    style={{ display: "none" }}
                    id={`sketch-upload-${question.id}`}
                    type="file"
                    onChange={handleFileChange}
                    disabled={isDisabled || question.isDisabled || !canAddMore}
                />
                {files.map((file) => (
                    <QuestionAttachmentList key={file.id} file={file} onChange={handleFileUpdate} removeNewRow={handleRemoveFile} />
                ))}
            </Grid>
        </>
    );
};

export default SketchQuestion;
